import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { Trash2 } from 'lucide-react'
import { api, HTTPError, ErrorResponse } from '../api/client'
import { useEntities } from '../context/EntitiesContext'
import { PromptPageHeader } from '../components/PromptPageHeader'
import { PromptPageNavigation } from '../components/PromptPageNavigation'

export function PromptSettingsPage () {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { prompts, folders, refreshPrompts } = useEntities()
  const prompt = id ? prompts.find(p => p.id === id) || null : null
  const currentFolder = prompt?.folderId ? folders.find(f => f.id === prompt.folderId) : null

  // Prompt edit state
  const [editPromptName, setEditPromptName] = useState('')
  const [editPromptDescription, setEditPromptDescription] = useState('')
  const [editPromptFolderId, setEditPromptFolderId] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    if (prompt) {
      setEditPromptName(prompt.name)
      setEditPromptDescription(prompt.description || '')
      setEditPromptFolderId(prompt.folderId || '')
    }
  }, [prompt])

  const handleSave = async () => {
    if (!prompt || !editPromptName.trim()) return
    setSaving(true)
    setError(null)
    setSuccessMessage(null)
    try {
      await api.prompts.update(prompt.id, {
        name: editPromptName.trim(),
        description: editPromptDescription.trim() || undefined,
        folderId: editPromptFolderId || null,
      })
      await refreshPrompts()
      setSuccessMessage('Settings saved successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      if (err instanceof HTTPError) {
        try {
          const errorData = await err.response.json() as ErrorResponse
          setError(errorData.error || 'Failed to save settings')
        } catch {
          setError('Failed to save settings')
        }
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!prompt) return
    if (!window.confirm(`Are you sure you want to delete prompt "${prompt.name}"? This will also delete all its variants, deployments, and scenarios.`)) return

    setSaving(true)
    try {
      await api.prompts.delete(prompt.id)
      await refreshPrompts()
      navigate('/')
    } catch (err) {
      if (err instanceof HTTPError) {
        try {
          const errorData = await err.response.json() as ErrorResponse
          setError(errorData.error || 'Failed to delete prompt')
        } catch {
          setError('Failed to delete prompt')
        }
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred')
      }
      setSaving(false)
    }
  }

  if (!prompt) {
    return (
      <div>
        <div className="alert-error">Prompt not found</div>
      </div>
    )
  }

  return (
    <div>
      <PromptPageHeader prompt={prompt} currentFolder={currentFolder} />
      <PromptPageNavigation promptId={id!} />

      {/* Settings Form */}
      <div className="max-w-2xl">
        {error && (
          <div className="alert-error mb-4">{error}</div>
        )}
        {successMessage && (
          <div className="alert-success mb-4">{successMessage}</div>
        )}

        <div className="space-y-6">
          <div>
            <label htmlFor="prompt-name" className="input-label">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="prompt-name"
              type="text"
              value={editPromptName}
              onChange={(e) => setEditPromptName(e.target.value)}
              className="input"
              placeholder="Prompt name"
            />
          </div>

          <div>
            <label htmlFor="prompt-description" className="input-label">
              Description
            </label>
            <textarea
              id="prompt-description"
              value={editPromptDescription}
              onChange={(e) => setEditPromptDescription(e.target.value)}
              className="input"
              rows={3}
              placeholder="Prompt description (optional)"
            />
          </div>

          <div>
            <label htmlFor="prompt-folder" className="input-label">
              Folder
            </label>
            <select
              id="prompt-folder"
              value={editPromptFolderId}
              onChange={(e) => setEditPromptFolderId(e.target.value)}
              className="input"
            >
              <option value="">No folder (Uncategorized)</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSave}
              disabled={saving || !editPromptName.trim()}
              className="btn btn-primary"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="mt-12 pt-6 border-t border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4">Danger Zone</h2>
          <div className="bg-red-900/10 border border-red-900/30 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-medium text-white mb-1">Delete this prompt</h3>
                <p className="text-sm text-gray-400">
                  Once you delete a prompt, there is no going back. This will delete all variants, deployments, and scenarios.
                </p>
              </div>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="btn btn-danger flex items-center gap-2 ml-4 whitespace-nowrap"
              >
                <Trash2 className="h-4 w-4" />
                Delete Prompt
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
